import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Package, Truck, CheckCircle, XCircle, Clock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderTrackingPage = () => {
  const { getCartCount } = useCart();
  const [searchParams] = useSearchParams();
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const autoSearchedRef = useRef(false);

  // Detect logged-in state (sentinel in localStorage; real JWT lives in httpOnly cookie)
  useEffect(() => {
    const token = localStorage.getItem('userToken');
    const userStr = localStorage.getItem('user');
    if (token && userStr) {
      try {
        const u = JSON.parse(userStr);
        setIsLoggedIn(true);
        setUserEmail((u.email || '').trim());
      } catch (_) {
        setIsLoggedIn(false);
      }
    }
  }, []);

  // Auto-fill from URL params (e.g. clicked from email link) and auto-search
  useEffect(() => {
    const oid = (searchParams.get('order_id') || '').trim();
    const em = (searchParams.get('email') || '').trim();
    if (oid) setOrderId(oid);
    if (em) setEmail(em);
    if (oid && !autoSearchedRef.current) {
      // If logged in → no email needed (backend matches via cookie).
      // Otherwise email from URL is required.
      if (isLoggedIn || em) {
        autoSearchedRef.current = true;
        setTimeout(() => doSearch(oid, em), 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isLoggedIn]);

  const doSearch = async (oid, em) => {
    setLoading(true);
    setError('');
    setOrder(null);
    try {
      // Logged-in users: no email param needed — backend matches via httpOnly cookie.
      // Guests: must pass ?email=<order_email> for IDOR-safe access.
      const url = isLoggedIn
        ? `${API_URL}/api/orders/${encodeURIComponent(oid)}`
        : `${API_URL}/api/orders/${encodeURIComponent(oid)}?email=${encodeURIComponent(em)}`;
      const response = await fetch(url);
      if (!response.ok) {
        setError(
          response.status === 404
            ? (isLoggedIn
                ? 'Sipariş bulunamadı. Bu sipariş hesabınıza ait olmayabilir.'
                : 'Sipariş bulunamadı. Sipariş numarası ve e-posta eşleşmiyor olabilir.')
            : 'Bir hata oluştu. Lütfen tekrar deneyin.'
        );
        return;
      }
      const data = await response.json();
      setOrder(data);
    } catch (err) {
      console.error('Error fetching order:', err);
      setError('Bağlantı hatası. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!orderId.trim()) {
      setError('Lütfen sipariş numaranızı girin');
      return;
    }
    if (!isLoggedIn && !email.trim()) {
      setError('Lütfen siparişteki e-posta adresinizi girin');
      return;
    }
    await doSearch(orderId.trim(), isLoggedIn ? userEmail : email.trim());
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      'pending': { 
        label: 'Beklemede', 
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        icon: Clock,
        description: 'Siparişiniz alındı ve işleme konuluyor'
      },
      'confirmed': { 
        label: 'Onaylandı', 
        color: 'bg-blue-100 text-blue-800 border-blue-300',
        icon: CheckCircle,
        description: 'Siparişiniz onaylandı ve hazırlanıyor'
      },
      'shipped': { 
        label: 'Kargoya Verildi', 
        color: 'bg-purple-100 text-purple-800 border-purple-300',
        icon: Truck,
        description: 'Siparişiniz kargoya verildi ve yolda'
      },
      'delivered': { 
        label: 'Teslim Edildi', 
        color: 'bg-green-100 text-green-800 border-green-300',
        icon: CheckCircle,
        description: 'Siparişiniz teslim edildi'
      },
      'cancelled': { 
        label: 'İptal Edildi', 
        color: 'bg-red-100 text-red-800 border-red-300',
        icon: XCircle,
        description: 'Sipariş iptal edildi'
      }
    };
    return statusMap[status] || statusMap['pending'];
  };

  const getStatusTimeline = (currentStatus) => {
    const statuses = ['pending', 'confirmed', 'shipped', 'delivered'];
    const currentIndex = statuses.indexOf(currentStatus);
    
    if (currentStatus === 'cancelled') {
      return ['pending', 'cancelled'];
    }
    
    return statuses.map((status, index) => ({
      status,
      completed: index <= currentIndex,
      ...getStatusInfo(status)
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header cartCount={getCartCount()} />

      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Sipariş Takibi</h1>
          <p className="text-gray-600">
            Sipariş numaranızı girerek siparişinizin durumunu öğrenebilirsiniz
          </p>
        </div>

        {/* Search Form */}
        <div className="max-w-2xl mx-auto mb-12">
          <form onSubmit={handleSearch} className="bg-white rounded-lg shadow-md p-6">
            {isLoggedIn && (
              <div
                data-testid="order-tracking-logged-in-banner"
                className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-center gap-2"
              >
                <CheckCircle size={16} className="text-emerald-600 flex-shrink-0" />
                <span>
                  <strong>{userEmail}</strong> olarak giriş yaptınız — sadece sipariş numarası yeterli.
                </span>
              </div>
            )}
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Sipariş Numarası
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="Örn: ORD-AB12CD34EF56"
                data-testid="order-tracking-order-id"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
              />
              {!isLoggedIn && (
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Sipariş e-posta adresi"
                  data-testid="order-tracking-email"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                />
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="order-tracking-submit"
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Aranıyor...
                  </>
                ) : (
                  <>
                    <Search size={20} />
                    Sorgula
                  </>
                )}
              </button>
            </div>
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
          </form>
        </div>

        {/* Order Details */}
        {order && (
          <div className="max-w-4xl mx-auto">
            {/* Status Card */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Sipariş #{order.order_id}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {new Date(order.created_at).toLocaleString('tr-TR')}
                  </p>
                </div>
                <div className={`px-4 py-2 rounded-lg border-2 font-semibold ${getStatusInfo(order.status).color}`}>
                  {getStatusInfo(order.status).label}
                </div>
              </div>

              {/* Timeline */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-4">Sipariş Durumu</h3>
                <div className="relative">
                  <div className="flex justify-between items-center">
                    {getStatusTimeline(order.status).map((step, index) => {
                      const Icon = step.icon;
                      return (
                        <div key={step.status} className="flex-1 relative">
                          <div className="flex flex-col items-center">
                            {/* Icon */}
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                              step.completed 
                                ? 'bg-red-600 border-red-600 text-white' 
                                : 'bg-gray-100 border-gray-300 text-gray-400'
                            }`}>
                              <Icon size={24} />
                            </div>
                            
                            {/* Label */}
                            <p className={`mt-2 text-xs font-semibold text-center ${
                              step.completed ? 'text-red-600' : 'text-gray-400'
                            }`}>
                              {step.label}
                            </p>
                          </div>

                          {/* Connector Line */}
                          {index < getStatusTimeline(order.status).length - 1 && (
                            <div className={`absolute top-6 left-1/2 w-full h-0.5 ${
                              step.completed ? 'bg-red-600' : 'bg-gray-300'
                            }`} style={{ transform: 'translateY(-50%)' }}></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Durum:</strong> {getStatusInfo(order.status).description}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer & Delivery Info */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Package size={20} />
                  Müşteri Bilgileri
                </h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Ad Soyad:</strong> {order.customer_info.first_name} {order.customer_info.last_name}</p>
                  <p><strong>E-posta:</strong> {order.customer_info.email}</p>
                  <p><strong>Telefon:</strong> {order.customer_info.phone}</p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Truck size={20} />
                  Teslimat Adresi
                </h3>
                <div className="space-y-1 text-sm">
                  <p>{order.customer_info.address}</p>
                  <p>{order.customer_info.city}</p>
                  {order.customer_info.zip_code && <p>Posta Kodu: {order.customer_info.zip_code}</p>}
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-semibold text-gray-800 mb-4">Sipariş Ürünleri ({order.items.length} ürün)</h3>
              <div className="space-y-4">
                {order.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 pb-4 border-b last:border-b-0">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{item.product_name}</p>
                      <p className="text-sm text-gray-600">Adet: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-red-600">{item.subtotal.toLocaleString('tr-TR')} TL</p>
                      <p className="text-xs text-gray-500">{item.price.toLocaleString('tr-TR')} TL × {item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t">
                <div className="flex justify-between items-center text-xl font-bold">
                  <span className="text-gray-800">Toplam:</span>
                  <span className="text-red-600">{order.total.toLocaleString('tr-TR')} TL</span>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Ödeme Yöntemi: <span className="font-semibold capitalize">{order.payment_method}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Info Box */}
        {!order && !error && (
          <div className="max-w-2xl mx-auto bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">💡 Sipariş Numaranızı Nerede Bulabilirsiniz?</h3>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Sipariş verdiğinizde gönderilen onay e-postasında</li>
              <li>Sipariş tamamlama ekranında</li>
              <li>Hesabınızda "Siparişlerim" bölümünde</li>
            </ul>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default OrderTrackingPage;
